import type { BasePageProps } from '@shared/components/BasePageProps';
import type { SnackBarConfig } from '@shared/components/Snackbar';
import { Close, Insights } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import styled from 'styled-components';
import { Page } from '@shared/components/Page';
import CustomSnackbar from '@shared/components/Snackbar';
import { theme } from '@shared/theme';
import { DealInsightStep } from '../welcome/components/onboarding-wizard/steps/DealInsightStep';
import ReviewSection from '../welcome/components/ReviewSection';
import { HourlyRateBanner } from './components/HourlyRateBanner';

interface MyReviewsPageProps extends BasePageProps {
  embedded: boolean;
}

const _Container = styled.div<{ embedded?: boolean }>`
  padding: ${(props) => (props.embedded ? '18px 0' : '32px 24px')};
`;

function MyReviewsPage(props: MyReviewsPageProps) {
  const seoTitle = props.embedded
    ? 'My Deal Retros'
    : 'My Deal Retros - Contributor Dashboard';
  const seoDescription =
    "View and manage all the company deal retros you've contributed to the Kandir platform. Track your contributions and their impact.";
  const [showDealInsightsModal, setShowDealInsightsModal] = useState(false);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();

  const handleSnackBarClose = (value: boolean) => {
    setSnackbarConfig({
      ...snackbarConfig,
      open: value,
    });
  };

  return (
    <>
      <Page
        embedded={props.embedded}
        title={seoTitle}
        description={seoDescription}
        subtitle={seoDescription}
        action={
          <Button
            variant="contained"
            startIcon={<Insights />}
            onClick={() => setShowDealInsightsModal(true)}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              px: 3,
              py: 1,
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
              },
            }}
          >
            Update Your Deal History
          </Button>
        }
        actionHelperText="Quickly list your deal experience"
      >
        <HourlyRateBanner />
        <ReviewSection hideFilters={false} parentPageName="my-reviews" />
      </Page>

      <Dialog
        open={showDealInsightsModal}
        onClose={() => setShowDealInsightsModal(false)}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '12px',
            height: '80vh',
            maxHeight: '800px',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Insights sx={{ color: theme.palette.primary.main }} />
            <Typography variant="h6">Add Quick Retro</Typography>
          </Box>
          <IconButton
            onClick={() => setShowDealInsightsModal(false)}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
            p: 3,
          }}
        >
          <DealInsightStep
            onNext={() => {
              setShowDealInsightsModal(false);
              setSnackbarConfig({
                open: true,
                type: 'success',
                message: 'Deal insights saved successfully!',
              });
            }}
            onSkip={() => setShowDealInsightsModal(false)}
          />
        </DialogContent>
      </Dialog>

      <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
    </>
  );
}

export default MyReviewsPage;
